import * as React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cn } from '@/lib/utils'

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full', className)}
    {...props}
  />
))
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-muted',
      className
    )}
    {...props}
  />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }

// Helper to generate avatar URL using UI Avatars service (no MD5 needed)
export function getAvatarUrl(name: string, email: string, size = 80): string {
  const displayName = name || email.split('@')[0] || 'User'
  // Use UI Avatars service - generates nice initials-based avatars
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=${size}&background=F92672&color=fff&bold=true`
}

// User Avatar with Gravatar fallback
interface UserAvatarProps {
  avatarUrl?: string | null
  email: string
  name?: string
  className?: string
  size?: number
}

export function UserAvatar({ avatarUrl, email, name, className, size = 32 }: UserAvatarProps) {
  const initial = (name || email || '?')[0].toUpperCase()
  const fallbackUrl = getAvatarUrl(name || '', email, size * 2) // 2x for retina

  return (
    <Avatar className={className} style={{ width: size, height: size }}>
      <AvatarImage src={avatarUrl || fallbackUrl} alt={name || email} />
      <AvatarFallback className="bg-monokai-pink/20 text-monokai-pink font-medium">
        {initial}
      </AvatarFallback>
    </Avatar>
  )
}
